import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { invoicePdfAttachmentsKey } from '../lib/invoice-pdf-upload';
import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { allocatedDownpaymentKey } from './use-allocated-downpayment';
import { appliedProvisionalKey } from './use-applied-provisional';
import type { InvoiceRow } from '../types';

export interface CorrectInvoiceMutationArgs {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId?: string | undefined;
}

export interface CorrectInvoiceInput {
  readonly invoice_number: string;
  readonly reason: string;
  // True when the caller replaced the attached PDF in the same session, so the
  // correction audit event can record the document change alongside the number.
  readonly document_replaced?: boolean;
}

// Corrects the invoice number on an issued payable invoice. Refreshes the same
// views a save touches, plus the PDF attachment list so the preview reflects any
// file the panel replaced alongside the number change.
export function useCorrectInvoiceMutation(args: CorrectInvoiceMutationArgs) {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, CorrectInvoiceInput>({
    mutationFn: (input) =>
      apiRequest<InvoiceRow>(`/invoices/${encodeURIComponent(args.invoiceId)}/correct`, {
        method: 'POST',
        body: {
          invoice_number: input.invoice_number,
          reason: input.reason,
          document_replaced: input.document_replaced ?? false,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order-advance-invoices', args.orderId] });
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
      void qc.invalidateQueries({ queryKey: invoicePdfAttachmentsKey(args.invoiceId) });
      if (args.blOrderId !== undefined) {
        void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
        void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
        void qc.invalidateQueries({ queryKey: appliedProvisionalKey(args.blOrderId, 'Purchase') });
      }
    },
  });
}
